import React, { useState } from "react";

import { FormControlLabel, Stack, Typography } from "@mui/material";

import Setting from "@app/analytics/account/components/Setting";
import SettingAlert from "@app/analytics/account/components/SettingAlert";
import SettingContent from "@app/analytics/account/components/SettingContent";
import SettingsModule from "@app/analytics/account/components/SettingsModule";
import { Divider } from "@app/analytics/account/components/styles";
import { SettingModule } from "@app/analytics/account/types";
import { getSettingDisabledOpts } from "@app/analytics/account/UserPreferences/utils";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import { useAuthenticated } from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type { User, UserEditOptions } from "@app/common/types";

import Switch from "@rui/Switch";
import TooltipWrapper from "@rui/TooltipWrapper";

import ReadAutoRecordingSelector from "./ReadAutoRecordingSelector";

const getDisabledOpts = (
  user: User,
  canEdit: boolean,
  editAccessKey: keyof UserEditOptions
) => {
  if (!user) return { disabled: true };

  // if field isn't populated, assume open access
  if (!user.editOptions) return { disabled: false };
  return getSettingDisabledOpts(
    canEdit && (user.editOptions[editAccessKey]?.length ?? 0) > 1
  );
};

const RecordingAndPlaybackModule = (props: SettingModule) => {
  const { user } = useAuthenticated();
  const { userSettings, updateUserSettings } = useUserSettings();

  const [showZoomConfirmDialogIfNeeded, setShowZoomConfirmDialogIfNeeded] =
    useState(false);

  const { canEditTranscriptionOptIn, canEditStoreRecordingOptIn } =
    useFeatureAccess();

  const { tooltip: transcriptTooltip, disabled: transcriptionEditDisabled } =
    getDisabledOpts(user, canEditTranscriptionOptIn, "transcriptionOptIn");

  const { tooltip: storeRecordingTooltip, disabled: storeRecordingDisabled } =
    getDisabledOpts(user, canEditStoreRecordingOptIn, "storeRecordingOptIn");

  const handleEnableTranscription = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateUserSettings.mutate({ transcriptionOptIn: e.target.checked });
  };

  const handleEnableStoreRecording = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateUserSettings.mutate({ storeRecordingOptIn: e.target.checked });
  };

  return (
    <SettingsModule {...props} spacing={0}>
      <Setting
        title="Playback"
        subtitle="Audio and video playback, along with transcription, is available after a meeting in the Meeting Report."
      >
        <SettingContent>
          <Divider />
          <FormControlLabel
            labelPlacement="start"
            sx={{ justifyContent: "space-between", marginRight: 0, py: 2 }}
            control={
              <TooltipWrapper arrow placement="right" title={transcriptTooltip}>
                <Switch
                  disabled={transcriptionEditDisabled}
                  checked={userSettings.transcriptionOptIn}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setShowZoomConfirmDialogIfNeeded(e.target.checked);
                    handleEnableTranscription(e);
                  }}
                  withStatusLabel
                />
              </TooltipWrapper>
            }
            label={
              <Typography
                variant="bodySecondaryReg"
                sx={{ color: "shades.neutral.600" }}
              >
                Enable transcription for meetings
              </Typography>
            }
          />
          <Divider />
          <FormControlLabel
            labelPlacement="start"
            sx={{ justifyContent: "space-between", marginRight: 0, pt: 2 }}
            control={
              <TooltipWrapper
                arrow
                placement="right"
                title={storeRecordingTooltip}
              >
                <Switch
                  disabled={storeRecordingDisabled}
                  checked={userSettings.storeRecordingOptIn}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setShowZoomConfirmDialogIfNeeded(e.target.checked);
                    handleEnableStoreRecording(e);
                  }}
                  withStatusLabel
                />
              </TooltipWrapper>
            }
            label={
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  variant="bodySecondaryReg"
                  sx={{ color: "shades.neutral.600" }}
                >
                  Enable audio and video playback
                </Typography>
              </Stack>
            }
          />
        </SettingContent>
        <SettingAlert
          severity="info"
          persist
          show
          margin={false}
          sx={{ mt: "8px" }}
        >
          Enabling transcription and audio & video playback will not
          retroactively apply to past meetings. Similarly, disabling these will
          not remove access to transcripts or playback for meetings where it was
          enabled.
        </SettingAlert>
      </Setting>
      <ReadAutoRecordingSelector
        showConfirmDialogIfNeeded={showZoomConfirmDialogIfNeeded}
      />
    </SettingsModule>
  );
};

export default RecordingAndPlaybackModule;
